import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Download, ExternalLink } from 'lucide-react'
import { useAuth } from '@/store/auth'
import { Reveal } from '@/components/landing/Reveal'
import {
  AppleMark,
  LinuxMark,
  VsCodeMark,
  WindowsMark,
} from '@/components/landing/PlatformMarks'
import { markDownloaded } from '@/services/install'
import {
  downloadUrl,
  fetchReleases,
  findBuild,
  formatSize,
  UNPUBLISHED,
  type ReleaseBuild,
  type ReleaseIndex,
} from '@/services/releases'

/** The Download page. */
export function DownloadPage() {
  return <DownloadSection />
}

/**
 * Asks the API which installers exist.
 *
 * <p>Every platform is answered from the same manifest. Nothing here links a
 * path and hopes it is there: a card only becomes a download once the API has
 * said the file is on disk, so a platform that has not been published yet says
 * so instead of handing the visitor a dead link.
 *
 * <p>A 503 from a fresh environment is a normal answer rather than an error —
 * it means nothing has been published.
 */
function useReleases() {
  const [state, setState] = useState<{
    status: 'loading' | 'ready' | 'unpublished' | 'error'
    index?: ReleaseIndex
  }>({ status: 'loading' })

  useEffect(() => {
    const controller = new AbortController()
    fetchReleases(controller.signal)
      .then((index) => {
        setState(index === UNPUBLISHED ? { status: 'unpublished' } : { status: 'ready', index })
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return
        setState({ status: 'error' })
      })
    return () => controller.abort()
  }, [])

  return state
}

/**
 * The three platforms, in the order they are shown. `arch` is what the card
 * says when there is no published build to describe; once there is, the size
 * from the manifest is appended to it.
 *
 * <p>Each carries its platform's own mark rather than an icon standing in for
 * it &mdash; see PlatformMarks, which explains why none of them come from the
 * icon set.
 */
const PLATFORMS = [
  { id: 'macos', icon: <AppleMark size={22} />, name: 'macOS', arch: 'Apple Silicon and Intel' },
  { id: 'windows', icon: <WindowsMark size={21} />, name: 'Windows', arch: 'x64' },
  { id: 'linux', icon: <LinuxMark size={23} />, name: 'Linux', arch: 'x64' },
]

/**
 * The editor extension, on the VS Code Marketplace.
 *
 * <p>Kept out of PLATFORMS and given a row of its own: it is an add-on to the
 * app rather than a fourth way of getting it, and sitting it in that line of
 * three would read as a choice between them.
 *
 * <p>It used to be a .vsix served from the release directory beside the
 * installers. Published properly it costs nothing to host, cannot go stale
 * against the manifest, and -- the reason that matters -- updates itself. A
 * hand-installed .vsix never does: whoever took one kept that version until
 * they went looking for another.
 */
const EXTENSION_ID = 'NevTan.nevtan-ai-code'

/** Opens the extension inside VS Code, on its own Extensions page. */
const EXTENSION_IN_VSCODE = `vscode:extension/${EXTENSION_ID}`

/** The web listing, for a browser that has no VS Code to hand over to. */
const EXTENSION_ON_MARKETPLACE =
  `https://marketplace.visualstudio.com/items?itemName=${EXTENSION_ID}`

const AFTER_INSTALL = [
  'NevTan AI checks your processor, memory, and free disk space',
  'You pick from the models it confirms will run',
  'It downloads and loads, and you start working',
]

/**
 * Where a card's button goes.
 *
 * <p>Signed out it goes to sign-in carrying the target, so the download
 * continues on the way back rather than dropping the visitor on a page with no
 * idea what they came for. No build means no href, and the card says why.
 */
function buildHref(build: ReleaseBuild | undefined, signedIn: boolean, id: string) {
  if (!build) return undefined
  if (signedIn) return downloadUrl(build)
  return `/login?next=${encodeURIComponent(`/download?start=${id}`)}`
}

type ReleaseStatus = 'loading' | 'ready' | 'unpublished' | 'error'

function DownloadSection() {
  // Asked once here rather than inside each group of cards, so the page makes
  // a single request for the one manifest they both read.
  const { status, index } = useReleases()
  const signedIn = useAuth((s) => s.status) === 'authenticated'
  const [params, setParams] = useSearchParams()

  /**
   * Only ever once, whatever the url still says.
   *
   * <p>Starting a download does not navigate: the response is an attachment,
   * so the browser saves it and leaves the page exactly where it was, `?start`
   * and all. That parameter is a request to download, and left in place it is
   * re-read on every reload -- one refresh, one more copy of the file. It is
   * cleared below, and the ref covers the gap before that lands, including
   * React's double-invoked effects in development.
   */
  const resumed = useRef(false)

  // Coming back from signing in, with the target they had picked. Resumed
  // rather than left for them to click again: they already asked once, and the
  // sign-in was our requirement rather than their errand.
  const resume = params.get('start')
  useEffect(() => {
    if (!signedIn || !resume || !index || resumed.current) return
    const build = findBuild(index, resume)
    if (!build) return

    resumed.current = true
    // Dropped before the download starts, so a reload during it is harmless.
    // `replace` so Back does not return to a url that downloads again.
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        next.delete('start')
        return next
      },
      { replace: true },
    )
    markDownloaded()
    window.location.href = downloadUrl(build)
  }, [signedIn, resume, index, setParams])

  return (
    <section
      id="download"
      className="scroll-mt-16 border-b border-brand-line bg-gradient-to-b from-brand-tint/40 via-brand-page to-brand-page"
    >
      <div className="nv-container py-16 sm:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <span className="nv-eyebrow">Get started</span>
          <h2 className="nv-h2 mt-3 text-[2rem] sm:text-[2.4rem]">Download NevTan AI</h2>
          <p className="mt-3 text-[15.5px] leading-relaxed text-brand-body">
            Install the app, let it check your computer, and run your first model locally in a few
            minutes. Free to use.
          </p>
        </div>

        <DownloadCards status={status} index={index} signedIn={signedIn} />
        <EditorExtensionCard />

        <div className="mx-auto mt-10 grid max-w-4xl gap-3 lg:grid-cols-[1fr_1fr]">
          <Reveal>
            <div className="h-full rounded-xl border border-brand-line bg-brand-paper p-5 shadow-card">
              <h3 className="text-[13.5px] font-bold text-brand-heading">
                What happens after you install
              </h3>
              <ol className="mt-3 space-y-2.5">
                {AFTER_INSTALL.map((step, index) => (
                  <li
                    key={step}
                    className="flex gap-3 text-[13px] leading-relaxed text-brand-muted"
                  >
                    <span className="mt-[1px] flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-tint text-[10.5px] font-bold text-brand-tint-ink">
                      {index + 1}
                    </span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          </Reveal>

          <Reveal delay={90}>
            <div className="h-full rounded-xl border border-brand-line bg-brand-paper p-5 shadow-card">
              <h3 className="text-[13.5px] font-bold text-brand-heading">Before you start</h3>
              <dl className="mt-3 space-y-2.5 text-[13px] leading-relaxed">
                {[
                  ['Memory', '8 GB runs a small model. 16 GB is comfortable.'],
                  ['Disk', '4-8 GB per model. You choose how many to keep.'],
                  ['Connection', 'Needed to install the app and download models, not to use them.'],
                  // Right-click then Open stopped working on macOS 15. Sending
                  // people to a gesture Apple removed leaves them stuck at a
                  // dialog whose only other button deletes the download.
                  [
                    'First open on macOS',
                    'macOS will say it cannot verify the app. Open System Settings, ' +
                      'go to Privacy & Security, scroll down and choose Open Anyway.',
                  ],
                  [
                    'First open on Windows',
                    'If SmartScreen warns you, choose More info, then Run anyway.',
                  ],
                ].map(([term, detail]) => (
                  <div key={term} className="flex flex-wrap gap-x-2">
                    <dt className="font-semibold text-brand-heading">{term}</dt>
                    <dd className="flex-1 text-brand-muted">{detail}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </Reveal>
        </div>

        <p className="mx-auto mt-8 max-w-2xl text-center text-[13px] leading-relaxed text-brand-body">
          Prefer not to install anything?{' '}
          <Link to="/login" className="font-semibold text-brand hover:underline">
            Use NevTan AI in your browser
          </Link>{' '}
          with a hosted model and your own API key.
        </p>

        <p className="mx-auto mt-3 max-w-2xl text-center text-[12px] leading-relaxed text-brand-muted">
          Each card shows what is available right now. Linux is supported in the code and can be
          built from source while its installer is prepared.
        </p>
      </div>
    </section>
  )
}

/**
 * The three platform cards, all reflecting live state: the API is asked what
 * is published, and each card shows what it can actually deliver rather than a
 * button that would 404.
 */
function DownloadCards({
  status,
  index,
  signedIn,
}: {
  status: ReleaseStatus
  index?: ReleaseIndex
  signedIn: boolean
}) {
  return (
    <div className="mx-auto mt-10 grid max-w-4xl gap-3 sm:grid-cols-3">
      {PLATFORMS.map((platform, position) => {
        const build = index ? findBuild(index, platform.id) : undefined
        const size = build ? formatSize(build.sizeBytes) : ''

        return (
          <Reveal key={platform.id} delay={position * 70}>
            <PlatformCard
              icon={platform.icon}
              name={platform.name}
              arch={size ? `${platform.arch} · ${size}` : platform.arch}
              href={buildHref(build, signedIn, platform.id)}
              action={build ? 'Download' : unavailableLabel(status, 'Build from source')}
            />
          </Reveal>
        )
      })}
    </div>
  )
}

/** A keyboard key, so a shortcut reads as one rather than as punctuation. */
function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="rounded border border-brand-line bg-brand-panel px-1 py-0.5 font-mono text-[10.5px] font-semibold text-brand-body">
      {children}
    </kbd>
  )
}

/**
 * How to install it.
 *
 * <p>Two steps rather than four. Before this the extension was a .vsix people
 * downloaded, and installing one of those is genuinely obscure -- a file that
 * does nothing when double-clicked, and a menu behind an unlabelled button in
 * a panel you may never have opened. Published to the Marketplace it is the
 * same gesture as any other extension.
 */
const VSCODE_STEPS: ReactNode[] = [
  <>
    In VS Code, open the <b className="font-semibold text-brand-heading">Extensions</b> view
    &mdash; <Kbd>Ctrl</Kbd> <Kbd>Shift</Kbd> <Kbd>X</Kbd> on Windows and Linux, <Kbd>⇧</Kbd>{' '}
    <Kbd>⌘</Kbd> <Kbd>X</Kbd> on macOS.
  </>,
  <>
    Search for <b className="font-semibold text-brand-heading">NevTan AI</b> and press
    {' '}<b className="font-semibold text-brand-heading">Install</b>. It updates itself from then
    on.
  </>,
]

/**
 * The VS Code extension, on a row of its own beneath the installers.
 *
 * <p>The install steps sit in a block of their own under a rule rather than as
 * a line beside the button: squeezed into that row they ran into the button and
 * were cut off, and they are the part of this card that actually has to be
 * read.
 *
 * <p>The card is always drawn, exactly as the three platform cards above it
 * are. It used to remove itself when nothing was published, which meant a
 * deployment that had not staged the file lost the whole card -- title, icon
 * and instructions -- while Linux, equally unavailable, still sat there saying
 * so. One of those is an honest empty state and the other looks like a bug.
 */
function EditorExtensionCard() {
  return (
    <Reveal delay={210}>
      <div className="mx-auto mt-3 max-w-4xl rounded-xl border border-brand-line bg-brand-paper p-5 shadow-card">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-brand-tint text-brand-tint-ink">
            <VsCodeMark size={22} />
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-[15px] font-bold text-brand-heading">NevTan AI for VS Code</h3>
              <span className="rounded bg-brand-tint px-1.5 py-0.5 text-[10.5px] font-bold text-brand-tint-ink">
                Extension
              </span>
            </div>
            <p className="mt-1 text-[13px] leading-relaxed text-brand-muted">
              Chat with NevTan AI without leaving your editor. Requires VS Code 1.85 or newer.
            </p>
          </div>

          {/* The first opens VS Code straight at the extension; the second is
              for a browser with no VS Code to hand over to, and for anyone who
              would rather read the listing before installing anything. */}
          <div className="flex shrink-0 flex-wrap gap-2">
            <a
              href={EXTENSION_IN_VSCODE}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-[12.5px] font-semibold text-white shadow-brand transition hover:bg-brand-dark"
            >
              <Download size={13} /> Install in VS Code
            </a>
            <a
              href={EXTENSION_ON_MARKETPLACE}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-brand-line-strong px-3.5 py-2 text-[12.5px] font-semibold text-brand-body transition hover:border-brand hover:text-brand"
            >
              Marketplace <ExternalLink size={12} />
            </a>
          </div>
        </div>

        <div className="mt-4 border-t border-brand-line pt-4">
          <h4 className="text-[12.5px] font-bold text-brand-heading">How to install it</h4>
          <ol className="mt-2.5 space-y-2">
            {VSCODE_STEPS.map((step, position) => (
              <li
                key={position}
                className="flex gap-2.5 text-[12.5px] leading-relaxed text-brand-muted"
              >
                <span className="mt-[1px] flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-tint text-[10.5px] font-bold text-brand-tint-ink">
                  {position + 1}
                </span>
                <span className="min-w-0">{step}</span>
              </li>
            ))}
          </ol>

          {/* For anyone who would rather not click through a UI. The id is the
              publisher and name from the Marketplace, not a filename. */}
          <p className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-brand-muted">
            Or, from a terminal:
            <code className="min-w-0 break-all rounded-md border border-brand-line bg-brand-panel px-1.5 py-0.5 font-mono text-[11px] text-brand-body">
              code --install-extension {EXTENSION_ID}
            </code>
          </p>
        </div>
      </div>
    </Reveal>
  )
}

/**
 * What a card says when it has no published build. The fallback differs by
 * target: an operating system can be built from source, an extension cannot.
 */
function unavailableLabel(status: ReleaseStatus, fallback: string): string {
  if (status === 'loading') return 'Checking for a build…'
  if (status === 'error') return 'Downloads unavailable'
  return fallback
}

function PlatformCard({
  icon,
  name,
  arch,
  href,
  action,
}: {
  icon: ReactNode
  name: string
  arch: string
  href?: string
  action: string
}) {
  const shell =
    'flex h-full flex-col items-center rounded-xl border bg-brand-paper p-6 text-center transition'

  // Without a href there is nothing to download, so the card states why
  // rather than offering a button that would lead nowhere.
  if (!href) {
    return (
      <div className={`${shell} border-brand-line shadow-card`}>
        <span className="text-brand-muted">{icon}</span>
        <span className="mt-3 text-[15px] font-bold text-brand-heading">{name}</span>
        <span className="text-[12px] text-brand-muted">{arch}</span>
        <span className="mt-4 rounded-lg border border-brand-line px-3.5 py-1.5 text-[12px] text-brand-muted">
          {action}
        </span>
      </div>
    )
  }

  return (
    <a
      href={href}
      // Recorded so the hero can tell a returning visitor from a new one.
      onClick={markDownloaded}
      // No `download` attribute: every href here is an API URL that sends its
      // own Content-Disposition, and the attribute would override the filename
      // the server chose.
      className={`${shell} border-brand/30 shadow-raised hover:-translate-y-0.5 hover:border-brand hover:shadow-float`}
    >
      <span className="text-brand">{icon}</span>
      <span className="mt-3 text-[15px] font-bold text-brand-heading">{name}</span>
      <span className="text-[12px] text-brand-muted">{arch}</span>
      <span className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-brand px-4 py-1.5 text-[12.5px] font-semibold text-white shadow-brand">
        <Download size={13} /> {action}
      </span>
    </a>
  )
}

